import type { AnnotatedGraph } from '@litlingo/react';
import { fetchCommThreadContext, fetchTeamsContextSummaries } from 'actions';
import { EXPAND_ICON } from 'constants/dashboardIcons';
import { MESSAGE_ICON } from 'constants/envelopeIcons';
import moment from 'moment';
import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCustomerDomain } from 'selectors/auth';
import {
  getCommunicationContext,
  getFilteredCorrespondence,
  getSingleCommunication,
} from 'selectors/communications';
import { getTeamsContextSummaries } from 'selectors/envelopes';
import { getRawThreadContext } from 'selectors/events';
import { useSelector } from 'store';
import { ThreadContext, UUID } from 'types';
import parseBody, { parseTeamsEmojis } from 'utils/strings';
import HighlightedSentenceComponent from './HighlightedSentence';

type ComponentProps = {
  eventId: UUID;
  commUuid: UUID;
  commPlatformUuid: string;
  contextPadding?: number;
  authorOnly?: boolean;
  highRiskOnly?: boolean;
  hideHighlight?: boolean;
  handleSentenceClick?: (line: AnnotatedGraph, nodes: number[]) => void;
};

const RenderThread: React.FC<ComponentProps> = ({
  eventId,
  commUuid,
  commPlatformUuid,
  contextPadding = 5,
  authorOnly = true,
  highRiskOnly = false,
  hideHighlight = false,
  handleSentenceClick,
}) => {
  const dispatch = useDispatch();
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const domain = useSelector(getCustomerDomain);
  const communicationSummary = useSelector((state) => getSingleCommunication(state, commUuid));
  const commThreadContext = useSelector((state) => getCommunicationContext(state, commUuid));
  const eventThreadContext = useSelector(getRawThreadContext(eventId));
  const filteredCorrespondence = useSelector((state) =>
    getFilteredCorrespondence(
      state,
      commUuid,
      eventId,
      contextPadding,
      commPlatformUuid,
      authorOnly
    )
  );

  const contextSummaries = useSelector(getTeamsContextSummaries);

  const rawCorrespondence = eventThreadContext || commThreadContext;

  useEffect(() => {
    if (eventId !== null && commUuid && !rawCorrespondence) {
      dispatch(fetchCommThreadContext({ eventId, commUuid }));
    }
  }, [commUuid, dispatch, eventId, rawCorrespondence]);

  useEffect(() => {
    if (filteredCorrespondence.length > 0) {
      dispatch(
        fetchTeamsContextSummaries({
          platformGuid: filteredCorrespondence.map((c) => c.id),
          limit: -1,
        })
      );
    }
  }, [dispatch, filteredCorrespondence]);

  const renderContent = (): JSX.Element[] | JSX.Element => {
    if (filteredCorrespondence.length === 0 && (eventId || commUuid)) {
      return (
        <HighlightedSentenceComponent
          eventId={eventId}
          commUuid={commUuid}
          fullMessageOptions="HIGHLIGHTED"
          highRiskOnly={highRiskOnly}
          hideHighlight={hideHighlight}
          handleSentenceClick={handleSentenceClick}
        />
      );
    }

    const groupedFilteredCorrespondence = filteredCorrespondence.reduce<ThreadContext[][]>(
      (acc, cur) => {
        const lastGroup = acc[acc.length - 1];
        const lastItemLastGroup = lastGroup[lastGroup.length - 1];
        if (lastGroup && lastItemLastGroup) {
          if (
            (cur.from?.user?.displayName &&
              cur.from?.user?.displayName !== lastItemLastGroup.from?.user?.displayName) ||
            moment(cur?.createdDateTime).diff(
              moment(lastItemLastGroup.createdDateTime),
              'minutes'
            ) >= 30
          ) {
            return [...acc, [cur]];
          }
          const temp = acc;
          temp[acc.length - 1].push(cur);
          return temp;
        }
        return [[cur]];
      },
      [[]]
    );

    return groupedFilteredCorrespondence.map((group) => {
      let baseItem = group[0];
      group.forEach((i) => {
        if (commPlatformUuid.includes(i.id)) {
          baseItem = i;
        }
      });

      return (
        <div
          key={`${baseItem.from?.user?.displayName}-${baseItem.createdDateTime}`}
          className="flex flex-col h-full w-4/5 text-body focus:outline-none pb-1"
        >
          <div
            className={`${
              `${commPlatformUuid}`.includes(baseItem.id) ? 'text-black' : ' text-litlingo-gray-5'
            }`}
          >
            <div
              className={`flex flex-row gap-1 pb-1${
                commPlatformUuid.includes(baseItem.id) ? ' text-black' : ''
              }`}
            >
              <span className="font-bold">{baseItem.from?.user?.displayName}</span>
              <span>•</span>
              <span>{moment(baseItem.createdDateTime).format('MMM DD hh:mm A')}</span>
            </div>
            {group.map((item) => {
              const contextMessage = Object.values(contextSummaries).find(
                (c) => c.envelope.platform_guid === item.id
              );

              const renderContextLink = contextMessage &&
                contextMessage.envelope.uuid !== envelopeId && (
                  <div className="flex flex-row gap-2 items-center">
                    {contextMessage.events.length > 0 && (
                      <span className="text-litlingo-gray-5 w-3 inline-flex">{MESSAGE_ICON}</span>
                    )}
                    <a
                      href={`${window.location.origin}/${domain}/communication-envelopes/${contextMessage.envelope.uuid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-3 inline-flex"
                    >
                      {EXPAND_ICON}
                    </a>
                  </div>
                );

              let parsedContent = item?.body?.content || '';
              parsedContent = parseTeamsEmojis(parsedContent);

              return (
                <>
                  {item.messageType === 'message' && (
                    <div className="pl-4">
                      <div className="py-1 text-left pl-6 border-l border-litlingo-gray-2">
                        {commPlatformUuid.includes(item.id) ? (
                          <>
                            {eventId ? (
                              <div className="flex flex-row items-start gap-2">
                                <div className="flex flex-col">
                                  <HighlightedSentenceComponent
                                    eventId={eventId}
                                    commUuid={commUuid}
                                    fullMessageOptions="HIGHLIGHTED"
                                    highRiskOnly={highRiskOnly}
                                    hideHighlight={hideHighlight}
                                    handleSentenceClick={handleSentenceClick}
                                  />
                                </div>
                                {renderContextLink}
                              </div>
                            ) : (
                              <div className="flex flex-row items-start gap-2">
                                <div className="flex flex-col">
                                  <ReactMarkdown renderers={{ link: () => <></> }}>
                                    {communicationSummary?.body
                                      ? parseBody(
                                          communicationSummary.body.substring(
                                            0,
                                            communicationSummary.body.length
                                          )
                                        )
                                      : ''}
                                  </ReactMarkdown>
                                </div>
                                {renderContextLink}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex flex-row items-start gap-4">
                            <div
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: parsedContent || '<span>BLOCKED</span>',
                              }}
                            />
                            {renderContextLink}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })}
          </div>
        </div>
      );
    });
  };

  return <div className="flex flex-col gap-x-4 pb-6">{renderContent()}</div>;
};

export default RenderThread;
